import SafeEventEmitter from '@metamask/safe-event-emitter'
import { EventEmitter } from 'events'
import { Ref } from 'vue'

export type Frame = Ref<InstanceType<typeof HTMLIFrameElement> | undefined>
export type Type = string
export type Mode = 'host' | 'client'

export interface Context {
  mode: Mode
  post: (type: Type, message?: any, winTarget?: Window) => void
  destroy: () => void
  iframeRef?: Frame
  iframeWindow?: Window
  events: EventEmitter
  remoteUrl?: string
}

export interface PostObject {
  id: string
  type: Type
  payload: Record<string, any>
}

export interface Options {
  id: string
  iframeRef?: Frame
  iframeWindow?: Window
  remoteUrl?: string
}

const iframeLoaded = 'iframe-loaded'

const register: Record<string, Context> = {}

const processMessage = async (event: MessageEvent<PostObject>) => {
  if (
    !event.data ||
    !event.data.id ||
    !register[event.data.id] ||
    ((register[event.data.id].remoteUrl ?? '*') !== '*' &&
      event.origin !== 'null' &&
      !event.origin.startsWith(register[event.data.id].remoteUrl as string))
  ) {
    return
  }
  const { id, type, payload } = event.data
  const { events } = register[id]
  events.emit(type, payload)
}

/**
 * A hook to communicate between an iframe and a parent window.
 *
 * @param Mode - 'client' (iframe) or 'host'
 * @param Options - object containg:
 * - id of the host/client,
 * - a remote URL to limit who can recieve/process Events,
 * - a vue template reference
 * {@link Options}
 *
 * Usage:
 * const { post } = useIFrameAPIConnector('client', {
 * id: EXTENSION_ORIGIN,
 * remoteUrl: getURL(),
 * })
 *
 * post('data', { data: ... })
 *
 * const { events } = useIFrameAPIConnector('host', {
 * id: EXTENSION_ORIGIN,
 * iframeRef: iframeRef,
 * remoteUrl: getURL(),
 * })
 *
 * events.on('data', (payload: unknown) => ...)
 *
 * DO NOT OPTIMIZE COMPLEXITY BECAUSE THIS HAS A LOT OF CLOSURES
 * TO MAKE EVENTS HAPPEN CORRECTLY
 */
export const useIFrameAPIConnector = (
  mode: Mode,
  options: Options
  // eslint-disable-next-line sonarjs/cognitive-complexity
): Context => {
  if (register[options.id]) {
    return register[options.id] as Context
  }

  const events = new SafeEventEmitter()
  const isHost = mode === 'host'
  let target: Window | null = window.parent

  const post = (type: Type, message?: any, customTarget?: Window) => {
    if (!target) {
      throw new Error('Target Window is unloaded')
    }

    const remote = options.remoteUrl || '*'

    if (message instanceof Error) {
      message = message.message
    }

    try {
      message = JSON.parse(JSON.stringify(message ?? {}))
    } catch {
      throw new Error('Message cannot be serialized to JSON')
    }

    const targetX = customTarget || target

    targetX.postMessage(
      {
        id: options.id,
        type,
        payload: message ?? {},
      },
      remote
    )
  }

  if (isHost && !options.iframeRef) {
    throw new Error('"host" mode requires an iFrame reference')
  }

  const destroy = () => {
    events.removeAllListeners()
    delete register[options.id]

    if (Object.keys(register).length === 0) {
      window.removeEventListener('message', processMessage)
    }
  }

  const context: Context = {
    mode,
    post,
    destroy,
    events,
    iframeRef: options.iframeRef,
    iframeWindow: options.iframeWindow,
    remoteUrl: options.remoteUrl,
  }

  register[options.id] = context

  if (Object.keys(register).length === 1) {
    window.addEventListener('message', processMessage)
  }

  if (isHost) {
    events.on(iframeLoaded, () => {
      const targetWindow = context.iframeWindow
      if (targetWindow) {
        target = targetWindow
      }
    })
  } else {
    post(iframeLoaded)
  }

  return context as Context
}
